/**
 * episodesApi.js — API layer for Resident Trends v2 episodes.
 *
 * fetchResidentEpisodes  — rep_type `residentepisodes` (episodes + nested
 *                          signals + summary + active mutes)
 * updateResidentEpisode  — lifecycle transition via /transaction
 *                          (RequestType `resident_episode_update`)
 * clearTrendMute         — clear an expected-behavior mute
 *                          (RequestType `resident_thread_unmute`)
 */

import { apiTransaction } from '../../services/api.js';
import { getAuthHeaders } from '../../services/auth.js';

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

/**
 * Fetch episodes for a campus (optionally scoped to one resident).
 *
 * @param {object} args
 * @param {number} args.campusId
 * @param {string} [args.status]     — omit for the open set; 'all' for history
 * @param {number} [args.residentId] — scope to one resident (profile page)
 * @param {number} [args.limit]
 * @param {number} [args.offset]
 * @returns {Promise<{episodes: Array, summary_items: Array, mutes: Array, wins: Array, insights: Array}>}
 */
export async function fetchResidentEpisodes({ campusId, status, residentId, limit, offset }) {
    const empty = { episodes: [], summary_items: [], mutes: [], wins: [], insights: [] };
    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: await getAuthHeaders(),
            body: JSON.stringify({
                rep_type: 'residentepisodes',
                campus_id: campusId,
                status: status ?? null,
                resident_id: residentId ?? null,
                limit: limit ?? null,
                offset: offset ?? null,
            }),
        });
        const raw = await resp.json();
        const result = raw?.Status?.toLowerCase() === 'ok' ? raw.Res : raw?.Res || {};
        return result && Array.isArray(result.episodes) ? result : empty;
    } catch (err) {
        console.error('[episodesApi] fetchResidentEpisodes error:', err);
        return empty;
    }
}

/**
 * Transition an episode's lifecycle status.
 *
 * @param {object} args
 * @param {number} args.campusId
 * @param {number} args.episodeId
 * @param {string} args.status        — 'acknowledged' | 'in_progress' | 'resolved' | 'dismissed'
 * @param {string} [args.actionTaken] — required for 'resolved' (ACTION_OPTIONS value)
 * @param {string} [args.dismissReason] — required for 'dismissed'
 * @param {number} [args.muteDays]    — mute duration for expected_behavior dismissals
 * @param {string} [args.notes]
 * @param {number} args.userId
 * @param {string} args.clientId
 * @returns {Promise<[boolean, string?]>}
 */
export function updateResidentEpisode({ campusId, episodeId, status, actionTaken, dismissReason, muteDays, notes, userId, clientId }) {
    return apiTransaction({
        transType: 'resident_episode_update',
        payload: [{
            episode_id: episodeId,
            status,
            action_taken: actionTaken || null,
            dismiss_reason: dismissReason || null,
            mute_days: muteDays || null,
            notes: notes || null,
        }],
        campusId,
        userId,
        clientId,
    });
}

/**
 * Acknowledge or close a community-level insight.
 *
 * @param {object} args
 * @param {number} args.campusId
 * @param {number} args.insightId
 * @param {string} args.status — 'acknowledged' | 'closed'
 * @param {number} args.userId
 * @param {string} args.clientId
 * @returns {Promise<[boolean, string?]>}
 */
export function updateCommunityInsight({ campusId, insightId, status, userId, clientId }) {
    return apiTransaction({
        transType: 'community_insight_update',
        payload: [{ insight_id: insightId, status }],
        campusId,
        userId,
        clientId,
    });
}

/**
 * Clear an expected-behavior mute from a resident's concern thread.
 *
 * @param {object} args
 * @param {number} args.campusId
 * @param {number} args.threadId
 * @param {number} args.userId
 * @param {string} args.clientId
 * @returns {Promise<[boolean, string?]>}
 */
export function clearTrendMute({ campusId, threadId, userId, clientId }) {
    return apiTransaction({
        transType: 'resident_thread_unmute',
        payload: [{ thread_id: threadId }],
        campusId,
        userId,
        clientId,
    });
}
